import type { WizardFormData } from '../page';

interface Props {
  formData: WizardFormData;
  onChange: (updates: Partial<WizardFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string;
}

const plans = [
  {
    id: 'free' as const,
    name: 'Gratuito',
    price: 'R$ 0',
    period: '/mês',
    description: 'Para começar a organizar suas finanças',
    features: [
      { text: '1 conta bancária', included: true },
      { text: '1 cartão de crédito', included: true },
      { text: '10 lançamentos por mês', included: true },
      { text: 'Dashboard básico', included: true },
      { text: '2 metas financeiras', included: true },
      { text: 'Relatórios avançados', included: false },
      { text: 'Lançamentos ilimitados', included: false },
      { text: 'Exportação de dados', included: false },
    ],
    badge: null,
    highlight: false,
  },
  {
    id: 'paid' as const,
    name: 'Premium',
    price: 'R$ 29,90',
    period: '/mês',
    description: 'Para quem quer controle financeiro completo',
    features: [
      { text: 'Contas ilimitadas', included: true },
      { text: 'Cartões ilimitados', included: true },
      { text: 'Lançamentos ilimitados', included: true },
      { text: 'Dashboard avançado', included: true },
      { text: 'Metas ilimitadas', included: true },
      { text: 'Relatórios avançados', included: true },
      { text: 'Exportação CSV/PDF', included: true },
      { text: 'Suporte prioritário', included: true },
    ],
    badge: 'Recomendado',
    highlight: true,
  },
];

export const WizardStep5 = ({ formData, onChange, onNext, onBack, loading = false, error }: Props) => {
  return (
    <div className="p-8">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-700 text-gray-900 mb-1">Escolha seu plano</h2>
        <p className="text-gray-500 text-sm">Você pode fazer upgrade a qualquer momento</p>
      </div>

      {error && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {plans.map((plan) => (
          <button
            key={plan.id}
            onClick={() => onChange({ plan: plan.id })}
            disabled={loading}
            className={`relative p-5 rounded-2xl border-2 text-left transition-all cursor-pointer disabled:opacity-60 ${
              formData.plan === plan.id
                ? 'border-emerald-500 bg-emerald-50'
                : plan.highlight
                ? 'border-emerald-200 hover:border-emerald-300'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {plan.badge && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-600 px-3 py-1 rounded-full whitespace-nowrap">
                {plan.badge}
              </span>
            )}
            <div className="mb-3">
              <p className="text-base font-700 text-gray-900">{plan.name}</p>
              <div className="flex items-baseline gap-0.5 mt-1">
                <span className="text-2xl font-800 text-gray-900">{plan.price}</span>
                <span className="text-xs text-gray-500">{plan.period}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
            </div>
            <ul className="space-y-2">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className={`w-4 h-4 flex items-center justify-center rounded-full shrink-0 ${
                    f.included ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <i className={`text-[10px] ${f.included ? 'ri-check-line text-emerald-600' : 'ri-close-line text-gray-400'}`} />
                  </div>
                  <span className={`text-xs ${f.included ? 'text-gray-700' : 'text-gray-400'}`}>{f.text}</span>
                </li>
              ))}
            </ul>
            {formData.plan === plan.id && (
              <div className="mt-3 flex items-center gap-1.5 text-emerald-600">
                <div className="w-4 h-4 flex items-center justify-center bg-emerald-500 rounded-full">
                  <i className="ri-check-line text-white text-[10px]" />
                </div>
                <span className="text-xs font-600">Selecionado</span>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="h-12 px-6 border border-gray-200 text-gray-700 font-600 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={loading}
          className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? (
            <><i className="ri-loader-4-line animate-spin text-base" />Ativando plano...</>
          ) : (
            <>Continuar<i className="ri-arrow-right-line text-base" /></>
          )}
        </button>
      </div>
    </div>
  );
};
